import "@/App.css";
import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { applyTheme, getSavedTheme } from "@/lib/ui";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import TenantLogin from "@/pages/TenantLogin";
import PublicForm from "@/pages/PublicForm";
import ResetPassword from "@/pages/ResetPassword";
import TenantNotFound from "@/pages/TenantNotFound";
import TrackRedirect from "@/pages/TrackRedirect";
const SuperAdmin = lazy(() => import("@/panels/SuperAdmin"));
const Tenant = lazy(() => import("@/panels/Tenant"));
const Publisher = lazy(() => import("@/panels/Publisher"));
const Manager = lazy(() => import("@/panels/Manager"));

function Boot() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--tf-bg)]">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-emerald-500" />
    </div>
  );
}

const HOME = { super_admin: "/superadmin", tenant_admin: "/app", manager: "/manager", publisher: "/publisher",
  operations: "/app", finance: "/app", reporting: "/app", viewer: "/app", tenant_manager: "/app" };

function Protected({ roles, children }) {
  const { user, booting } = useAuth();
  if (booting || user === null) return <Boot />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={HOME[user.role] || "/login"} replace />;
  return children;
}

function RootRedirect({ hostTenant }) {
  const { user, booting } = useAuth();
  if (booting || user === null) return <Boot />;
  if (!user) return <Navigate to={hostTenant ? `/login/${hostTenant}` : "/login"} replace />;
  return <Navigate to={HOME[user.role] || "/login"} replace />;
}

function AppRoutes({ hostTenant }) {
  return (
    <Suspense fallback={<Boot />}>
      <Routes>
        <Route path="/login" element={hostTenant ? <Navigate to={`/login/${hostTenant}`} replace /> : <Login />} />
        <Route path="/login/:slug" element={<TenantLogin portal="staff" />} />
        <Route path="/p/:slug" element={<TenantLogin portal="publisher" />} />
        <Route path="/f/:code" element={<PublicForm />} />
        <Route path="/t/:code" element={<TrackRedirect />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/superadmin" element={<Protected roles={["super_admin"]}><SuperAdmin /></Protected>} />
        <Route path="/app" element={<Protected roles={["tenant_admin", "operations", "finance", "reporting", "viewer", "tenant_manager"]}><Tenant /></Protected>} />
        <Route path="/publisher" element={<Protected roles={["publisher"]}><Publisher /></Protected>} />
        <Route path="/manager" element={<Protected roles={["manager"]}><Manager /></Protected>} />
        <Route path="/" element={<RootRedirect hostTenant={hostTenant} />} />
        <Route path="*" element={<RootRedirect hostTenant={hostTenant} />} />
      </Routes>
    </Suspense>
  );
}

function HostGate() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const host = window.location.hostname;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/resolve-host?host=${encodeURIComponent(host)}`)
      .then((r) => r.json()).then(setInfo).catch(() => setInfo({ type: "main" }));
  }, []);
  if (!info) return <Boot />;
  if (info.type === "redirect") { window.location.replace(info.url); return <Boot />; }
  if (info.type === "not_found") return <TenantNotFound />;
  if (info.type === "suspended") return <TenantNotFound suspended tenant={info.tenant} />;
  const hostTenant = info.type === "tenant" ? (info.tenant?.subdomain || info.tenant?.tenant_slug) : null;
  return <AppRoutes hostTenant={hostTenant} />;
}

function App() {
  useEffect(() => { applyTheme(getSavedTheme()); }, []);
  return (
    <div className="App">
      <Toaster theme="dark" position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <HostGate />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
export default App;
